<template>
  <div class="settings">
    <div class="page-header">
      <h1>Settings</h1>
    </div>

    <div class="settings-container">
      <div class="settings-section">
        <h2>Notifications</h2>

        <div class="setting-item">
          <div class="setting-info">
            <h3>Push Notifications</h3>
            <p>Receive notifications for your favorite teams' games</p>
          </div>
          <div class="setting-control">
            <button
              v-if="!notificationPermission || notificationPermission === 'default'"
              @click="requestNotificationPermission"
              class="btn btn-primary"
              :disabled="isLoading"
            >
              <i v-if="isLoading" class="fas fa-spinner fa-spin"></i>
              <span v-if="isLoading">Enabling...</span>
              <span v-else>Enable Notifications</span>
            </button>
            <div
              v-else-if="notificationPermission === 'granted'"
              class="subscription-status"
            >
              <div v-if="subscriptionStatus === 'subscribed'" class="permission-status">
                <i class="fas fa-check-circle"></i>
                <span>Push Notifications Active</span>
              </div>
              <div v-else-if="subscriptionStatus === 'error'" class="permission-status error">
                <i class="fas fa-exclamation-triangle"></i>
                <span>Setup Failed</span>
                <button @click="retrySubscription" class="btn btn-secondary btn-sm">Retry</button>
              </div>
              <div v-else class="permission-status warning">
                <i class="fas fa-spinner fa-spin"></i>
                <span>Setting up...</span>
                <button @click="retrySubscription" class="btn btn-secondary btn-sm">Try Again</button>
              </div>
            </div>
            <div v-else class="permission-status denied">
              <i class="fas fa-times-circle"></i>
              <span>Notifications Blocked</span>
            </div>
          </div>
        </div>

        <div v-if="notificationPermission === 'granted'" class="setting-item">
          <div class="setting-info">
            <h3>Favorite Team Alerts</h3>
            <p>Get notified when favorite teams start playing</p>
          </div>
          <div class="setting-control">
            <label class="switch">
              <input
                type="checkbox"
                v-model="favoriteTeamAlerts"
                @change="saveSettings"
              />
              <span class="slider"></span>
            </label>
          </div>
        </div>

        <div v-if="notificationPermission === 'granted'" class="setting-item">
          <div class="setting-info">
            <h3>Score Updates</h3>
            <p>Receive score updates for live games</p>
          </div>
          <div class="setting-control">
            <label class="switch">
              <input
                type="checkbox"
                v-model="scoreUpdates"
                @change="saveSettings"
              />
              <span class="slider"></span>
            </label>
          </div>
        </div>

        <div v-if="notificationPermission === 'granted'" class="setting-item">
          <div class="setting-info">
            <h3>Game Start Reminders</h3>
            <p>Get notified 15 minutes before games start</p>
          </div>
          <div class="setting-control">
            <label class="switch">
              <input
                type="checkbox"
                v-model="gameReminders"
                @change="saveSettings"
              />
              <span class="slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <h2>Time & Date</h2>

        <div class="setting-item">
          <div class="setting-info">
            <h3>Timezone</h3>
            <p>Display game times in your preferred timezone</p>
          </div>
          <div class="setting-control">
            <select
              v-model="selectedTimezone"
              @change="saveTimezoneSettings"
              class="timezone-select"
            >
              <option value="">Auto-detect (System Timezone)</option>
              <optgroup label="Common US Timezones">
                <option
                  v-for="tz in commonTimezones"
                  :key="tz.value"
                  :value="tz.value"
                >
                  {{ tz.label }}
                </option>
              </optgroup>
              <optgroup label="All Timezones">
                <option
                  v-for="tz in allTimezones"
                  :key="tz.value"
                  :value="tz.value"
                >
                  {{ tz.label }} ({{ tz.region }})
                </option>
              </optgroup>
            </select>
          </div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <h3>Time Format</h3>
            <p>Choose between 12-hour and 24-hour time format</p>
          </div>
          <div class="setting-control">
            <div class="radio-group">
              <label class="radio-label">
                <input
                  type="radio"
                  v-model="use12HourFormat"
                  :value="true"
                  @change="saveTimezoneSettings"
                />
                <span class="radio-text">12-hour (AM/PM)</span>
              </label>
              <label class="radio-label">
                <input
                  type="radio"
                  v-model="use12HourFormat"
                  :value="false"
                  @change="saveTimezoneSettings"
                />
                <span class="radio-text">24-hour</span>
              </label>
            </div>
          </div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <h3>Current Settings</h3>
            <p>Preview how dates and times will appear</p>
          </div>
          <div class="setting-control">
            <div class="timezone-preview">
              <div class="preview-item">
                <span class="preview-label">Time:</span>
                <span class="preview-value">{{ exampleTime }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">Date:</span>
                <span class="preview-value">{{ exampleDate }}</span>
              </div>
              <div class="preview-item" v-if="currentTimezone">
                <span class="preview-label">Timezone:</span>
                <span class="preview-value">{{ currentTimezoneLabel }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <h2>Favorite Teams</h2>

        <div class="setting-item">
          <div class="setting-info">
            <h3>Favorite Teams</h3>
            <p>Teams you follow for quick access to games and notifications</p>
          </div>
          <div class="setting-control">
            <div v-if="favoriteTeams.length === 0" class="no-favorites">
              <p>No favorite teams selected yet</p>
              <p class="hint">
                Visit the home page and click the star icon on games to add
                favorite teams
              </p>
            </div>
            <div v-else class="favorite-teams-list">
              <div
                v-for="team in favoriteTeams"
                :key="team.id"
                class="favorite-team-item"
              >
                <div class="team-info">
                  <img
                    :src="getTeamLogo(team)"
                    :alt="team.name"
                    class="team-logo"
                    @error="handleImageError"
                  />
                  <span class="team-name">{{ team.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <h2>Testing</h2>
        <div class="setting-item">
          <div class="setting-info">
            <h3>Test Notification</h3>
            <p>Send a test notification to your device</p>
          </div>
          <div class="setting-control">
            <button
              @click="sendTestNotification"
              class="btn btn-secondary"
              :disabled="!hasPermission"
            >
              Send Test
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { notificationService } from "../utils/notifications";
import { cookieUtils } from "../utils/cookieUtils";
import { timezoneUtils } from "../utils/timezoneUtils";

export default {
  name: "SettingsView",
  data() {
    return {
      notificationPermission: null,
      favoriteTeamAlerts: false,
      scoreUpdates: false,
      gameReminders: false,
      selectedTimezone: "",
      use12HourFormat: true,
      commonTimezones: timezoneUtils.commonTimezones,
      allTimezones: timezoneUtils.allTimezones,
      favoriteTeams: [],
      isSubscribed: false,
      subscriptionStatus: 'unknown',
      isLoading: false,
    };
  },
  computed: {
    hasPermission() {
      return notificationService.hasPermission();
    },
    currentTimezone() {
      return this.selectedTimezone || timezoneUtils.getUserTimezone();
    },
    currentTimezoneLabel() {
      if (this.selectedTimezone) {
        const commonTz = timezoneUtils.findCommonTimezone(
          this.selectedTimezone
        );
        const allTz = timezoneUtils.findAllTimezone(this.selectedTimezone);
        return commonTz
          ? commonTz.label
          : allTz
          ? allTz.label
          : this.selectedTimezone;
      }
      return `Auto-detect (${timezoneUtils.getUserTimezone()})`;
    },
    exampleTime() {
      const now = new Date();
      return timezoneUtils.formatTime(now, this.currentTimezone, {
        hour12: this.use12HourFormat,
      });
    },
    exampleDate() {
      const now = new Date();
      return timezoneUtils.formatDate(now, this.currentTimezone);
    },
  },
  async created() {
    this.checkNotificationStatus();
    this.loadSettings();
    this.loadTimezoneSettings();
    this.loadFavoriteTeams();

    // Check subscription status if notifications are enabled
    if (this.notificationPermission === 'granted') {
      await this.checkSubscriptionStatus();
    }
  },
  methods: {
    async checkNotificationStatus() {
      if (notificationService.isSupported()) {
        this.notificationPermission = Notification.permission;
      } else {
        this.notificationPermission = "unsupported";
      }
    },

    async requestNotificationPermission() {
      this.isLoading = true;
      try {
        const granted = await notificationService.requestPermission();
        this.notificationPermission = granted ? "granted" : "denied";

        if (granted) {
          this.loadSettings();
          // Try to subscribe to push notifications with Firebase
          await this.subscribeToPushNotifications();
        }
      } catch (error) {
        console.error('Error requesting notification permission:', error);
        this.subscriptionStatus = 'error';
      } finally {
        this.isLoading = false;
      }
    },

    loadSettings() {
      const settings = cookieUtils.getNotificationSettings();
      this.favoriteTeamAlerts = settings.favoriteTeamAlerts || false;
      this.scoreUpdates = settings.scoreUpdates || false;
      this.gameReminders = settings.gameReminders || false;
    },

    async saveSettings() {
      const settings = {
        favoriteTeamAlerts: this.favoriteTeamAlerts,
        scoreUpdates: this.scoreUpdates,
        gameReminders: this.gameReminders,
      };
      cookieUtils.saveNotificationSettings(settings);

      // Update backend notification preferences if subscribed
      if (this.subscriptionStatus === 'subscribed') {
        try {
          await this.updateNotificationPreferences(settings);
        } catch (error) {
          console.error('Error updating notification preferences:', error);
        }
      }
    },

    async sendTestNotification() {
      try {
        await notificationService.sendTestNotification(
          "CFB Scores Test",
          "This is a test notification from CFB Scores Vibe!"
        );
      } catch (error) {
        console.error('Error sending test notification:', error);
        // Show error feedback to user
        alert('Failed to send test notification. Please check your subscription status.');
      }
    },

    loadTimezoneSettings() {
      this.selectedTimezone = cookieUtils.getUserSelectedTimezone() || "";
      this.use12HourFormat = cookieUtils.getUse12HourFormat();
    },

    saveTimezoneSettings() {
      cookieUtils.setUserSelectedTimezone(this.selectedTimezone);
      cookieUtils.setUse12HourFormat(this.use12HourFormat);
    },

    loadFavoriteTeams() {
      this.favoriteTeams = cookieUtils.getFavoriteTeams();
    },

    getTeamLogo(team) {
      // Try to construct the ESPN logo URL based on team ID
      if (team.id) {
        return `https://a.espncdn.com/i/teamlogos/ncaa/500/${team.id}.png`;
      }
      // Fallback to logo property if available
      return team.logo || "";
    },

    handleImageError(event) {
      // Set a default placeholder or remove the broken image
      event.target.style.display = "none";
    },

    // Firebase Push Notification Methods
    async checkSubscriptionStatus() {
      try {
        const userId = notificationService.getUserId();
        if (!userId) {
          this.subscriptionStatus = 'unknown';
          return;
        }

        const response = await fetch(`/api/push/status?user_id=${userId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.count > 0) {
            this.subscriptionStatus = 'subscribed';
            // Load preferences from backend
            await this.loadBackendPreferences();
          } else {
            this.subscriptionStatus = 'unknown';
          }
        } else {
          this.subscriptionStatus = 'error';
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        this.subscriptionStatus = 'error';
      }
    },

    async subscribeToPushNotifications() {
      try {
        this.subscriptionStatus = 'pending';
        const result = await notificationService.subscribeToPushNotifications();

        if (result.success) {
          this.subscriptionStatus = 'subscribed';
          // Update backend with current settings and favorite teams
          await this.updateNotificationPreferences({
            favoriteTeamAlerts: this.favoriteTeamAlerts,
            scoreUpdates: this.scoreUpdates,
            gameReminders: this.gameReminders,
            teams: this.favoriteTeams.map(team => team.id)
          });
        } else {
          this.subscriptionStatus = 'error';
          throw new Error(result.error || 'Subscription failed');
        }
      } catch (error) {
        console.error('Error subscribing to push notifications:', error);
        this.subscriptionStatus = 'error';
        throw error;
      }
    },

    async retrySubscription() {
      try {
        await this.subscribeToPushNotifications();
      } catch (error) {
        // Error is already handled in subscribeToPushNotifications
      }
    },

    async updateNotificationPreferences(settings) {
      try {
        const userId = notificationService.getUserId();
        if (!userId) return;

        const payload = {
          user_id: userId,
          favorite_team_alerts: settings.favoriteTeamAlerts,
          score_updates: settings.scoreUpdates,
          game_reminders: settings.gameReminders,
          teams: settings.teams || this.favoriteTeams.map(team => team.id)
        };

        const response = await fetch('/api/push/preferences', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error('Failed to update preferences');
        }

        const result = await response.json();
        return result;
      } catch (error) {
        console.error('Error updating notification preferences:', error);
        throw error;
      }
    },

    async loadBackendPreferences() {
      try {
        const userId = notificationService.getUserId();
        if (!userId) return;

        const response = await fetch(`/api/push/preferences?user_id=${userId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.preferences) {
            const prefs = data.preferences;
            this.favoriteTeamAlerts = prefs.favorite_team_alerts;
            this.scoreUpdates = prefs.score_updates;
            this.gameReminders = prefs.game_reminders;
          }
        }
      } catch (error) {
        console.error('Error loading backend preferences:', error);
      }
    },
  },
};
</script>

<style scoped>
.settings {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
}

.page-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #30363d;
}

.page-header h1 {
  color: #c9d1d9;
  font-size: 2rem;
  margin: 0;
}

.settings-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.settings-section {
  background-color: #161b22;
  border-radius: 8px;
  border: 1px solid #30363d;
  overflow: hidden;
}

.settings-section h2 {
  color: #c9d1d9;
  font-size: 1.25rem;
  margin: 0;
  padding: 1rem 1.5rem;
  background-color: #0d1117;
  border-bottom: 1px solid #30363d;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #30363d;
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-info h3 {
  color: #e0e0e0;
  font-size: 1rem;
  margin: 0 0 0.25rem 0;
}

.setting-info p {
  color: #8b949e;
  font-size: 0.875rem;
  margin: 0;
}

.setting-control {
  display: flex;
  align-items: center;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #238636;
  color: white;
  border: 1px solid #2ea043;
}

.btn-primary:hover {
  background-color: #2ea043;
}

.btn-secondary {
  background-color: #21262d;
  color: #e0e0e0;
  border: 1px solid #30363d;
}

.btn-secondary:hover {
  background-color: #30363d;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.permission-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #56d364;
}

.permission-status.denied {
  color: #f85149;
}

.permission-status.error {
  color: #f85149;
}

.permission-status.warning {
  color: #d29922;
}

.permission-status i {
  font-size: 1rem;
}

.subscription-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

/* Toggle switch */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #30363d;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #238636;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

/* Timezone Select */
.timezone-select {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: 1px solid #30363d;
  background-color: #21262d;
  color: #e0e0e0;
  font-size: 0.875rem;
  min-width: 200px;
}

.timezone-select:focus {
  outline: none;
  border-color: #58a6ff;
  box-shadow: 0 0 0 2px rgba(88, 166, 255, 0.2);
}

/* Radio Group */
.radio-group {
  display: flex;
  gap: 1rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #e0e0e0;
}

.radio-label input[type="radio"] {
  accent-color: #238636;
}

.radio-text {
  user-select: none;
}

/* Timezone Preview */
.timezone-preview {
  background-color: #0d1117;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 1rem;
  min-width: 200px;
}

.preview-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.preview-item:last-child {
  margin-bottom: 0;
}

.preview-label {
  font-size: 0.875rem;
  color: #8b949e;
  font-weight: 500;
}

.preview-value {
  font-size: 0.875rem;
  color: #e0e0e0;
  font-weight: 500;
}

/* Favorite Teams */
.no-favorites {
  text-align: center;
  color: #8b949e;
}

.no-favorites p {
  margin: 0.5rem 0;
}

.no-favorites .hint {
  font-size: 0.8rem;
  color: #6e7681;
  font-style: italic;
}

.favorite-teams-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 300px;
}

.favorite-team-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: #0d1117;
  border: 1px solid #30363d;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.favorite-team-item:hover {
  background-color: #161b22;
}

.team-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.team-logo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: contain;
  background-color: #21262d;
  padding: 2px;
}

.team-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #e0e0e0;
  flex: 1;
}

@media (max-width: 768px) {
  .favorite-teams-list {
    max-width: 100%;
  }

  .favorite-team-item {
    padding: 0.5rem;
  }

  .team-logo {
    width: 28px;
    height: 28px;
  }

  .team-name {
    font-size: 0.8rem;
  }
}

@media (max-width: 768px) {
  .setting-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .setting-control {
    align-self: flex-start;
  }

  .radio-group {
    flex-direction: column;
    gap: 0.5rem;
  }

  .timezone-select {
    min-width: 100%;
  }

  .timezone-preview {
    min-width: 100%;
  }

  .favorite-teams-list {
    max-width: 100%;
  }

  .favorite-team-item {
    padding: 0.5rem;
  }

  .team-logo {
    width: 28px;
    height: 28px;
  }

  .team-name {
    font-size: 0.8rem;
  }
}
</style>
